import React, { useRef, useEffect, useMemo } from 'react';
import { useGLTF, Environment } from '@react-three/drei';
import * as THREE from 'three';
import ClickDetector from './ClickDetector';

const TARGET_SIZE = 4;

interface BodyModelProps {
  setModalData: React.Dispatch<
    React.SetStateAction<{ muscle: string; isOpen: boolean }>
  >;
}

const BodyModel: React.FC<BodyModelProps> = ({ setModalData }) => {
  const { scene } = useGLTF('/assets/model_female/female_2.gltf');
  const groupRef = useRef<THREE.Group>(null);
  const model = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse(child => {
      if (!(child as THREE.Mesh).isMesh) return;
      const mesh = child as THREE.Mesh;
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map(material => material.clone())
        : mesh.material.clone();
    });
    return clone;
  }, [scene]);

  useEffect(() => {
    if (!groupRef.current) return;

    // — Center & scale —
    const box = new THREE.Box3().setFromObject(groupRef.current);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const scaleFactor = TARGET_SIZE / Math.max(size.x, size.y, size.z);

    groupRef.current.scale.setScalar(scaleFactor);
    groupRef.current.position.set(
      -center.x * scaleFactor,
      -center.y * scaleFactor,
      -center.z * scaleFactor
    );

    // — Darken & tone-down reflections per material —
    groupRef.current.traverse(child => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];

        mats.forEach(mat => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            mat.roughness = 1.0; // make it fully matte
            mat.metalness = 0.0; // non-metal
            mat.color.multiplyScalar(0.64); // safe: this is a fresh material clone
            mat.envMapIntensity = 0.18;
          }
        });
      }
    });
  }, [model]);

  return (
    <group ref={groupRef}>
      {/* Very soft ambient */}
      <ambientLight intensity={0.28} />

      {/* Subtle sky/ground fill */}
      <hemisphereLight
        skyColor="#ffffff"
        groundColor="#444444"
        intensity={0.25}
      />

      {/* One low-power directional */}
      <directionalLight position={[5, 10, 5]} intensity={0.42} />

      {/* HDRI studio (no `intensity` prop here) */}
      <Environment preset="studio" background={false} />

      {/* Model + click logic */}
      <primitive object={model} />
      <ClickDetector model={model} setModalData={setModalData} />
    </group>
  );
};

export default BodyModel;
